import { motion } from 'framer-motion'

// Wardrobe items - 3 faded (forgotten) + 1 highlighted (resurfaced)
const forgottenItems = [
    { id: 1, image: '/wardrobe/striped-tee.jpg', alt: 'Striped tee' },
    { id: 2, image: '/wardrobe/striped-shirt.jpg', alt: 'Striped shirt' },
    { id: 3, image: '/wardrobe/tan-jacket.jpg', alt: 'Tan jacket' },
]

const resurfacedItem = {
    id: 4,
    image: '/wardrobe/umbro-jacket.jpg',
    alt: 'Umbro jacket',
}

// Outfit card items (for the context section)
const outfitItems = [
    { color: '#3D4852', label: 'Navy coat' },
    { color: '#F5F0E8', label: 'Cream knit' },
    { color: '#4A5568', label: 'Dark jeans', image: '/textures/jeans.webp' },
]

export function StylistIntelligence() {
    return (
        <section className="py-24 sm:py-32 bg-paper">
            <div className="max-w-3xl mx-auto px-8 sm:px-6 lg:px-8">
                
                {/* Section Header */}
                <div className="mb-16 sm:mb-24">
                    <motion.p 
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        className="text-xs tracking-widest uppercase text-ink-tertiary mb-6"
                    >
                        How Michela styles you
                    </motion.p>

                    <motion.h2 
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="font-display text-3xl sm:text-4xl lg:text-display-lg font-semibold text-ink leading-tight mb-5"
                    >
                        Outfits that make sense for your day.
                    </motion.h2>

                    <motion.p 
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.15 }}
                        className="text-base sm:text-lg text-ink-tertiary leading-relaxed max-w-lg"
                    >
                        Michela starts with where you're going, the weather, and how you want to feel — then builds from what you already own.
                    </motion.p>
                </div>

                {/* Three Proof Moments */}
                <div className="space-y-24 sm:space-y-32">
                    
                    {/* Proof 1: Context → Outfit */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        <h3 className="text-xl font-medium text-ink mb-3">
                            The situation comes first
                        </h3>
                        <p className="text-base text-ink-tertiary mb-6 max-w-sm">
                            Michela checks the weather, understands the dress code, and factors in your comfort — before suggesting a single piece.
                        </p>
                        
                        {/* Context Chips */}
                        <div className="flex flex-wrap gap-2 mb-6">
                            {['Dinner with friends', 'London', '4°C', 'Light drizzle'].map((tag, i) => (
                                <motion.span
                                    key={tag}
                                    initial={{ opacity: 0, y: 4 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.15 + i * 0.05 }}
                                    className="px-3 py-1.5 bg-paper-alt border border-paper-tertiary rounded-full text-sm text-ink-secondary"
                                >
                                    {tag}
                                </motion.span>
                            ))}
                        </div>

                        {/* Outfit Card */}
                        <motion.div 
                            initial={{ opacity: 0, y: 8 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3 }}
                            className="bg-paper-alt rounded-2xl p-5 border border-paper-tertiary max-w-xs"
                        >
                            <div className="flex gap-2.5 mb-4">
                                {outfitItems.map((item, i) => (
                                    <div
                                        key={i}
                                        className="w-14 h-14 rounded-xl border border-paper-tertiary bg-cover bg-center"
                                        style={{ 
                                            backgroundColor: item.color,
                                            backgroundImage: 'image' in item ? `url(${item.image})` : undefined
                                        }}
                                    />
                                ))}
                            </div>
                            <p className="text-sm font-medium text-ink">Navy coat, cream knit, dark jeans</p>
                            <p className="text-xs text-ink-tertiary mt-1">Layered for warmth, polished for dinner</p>
                        </motion.div>
                    </motion.div>

                    {/* Proof 2: Built from your wardrobe */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        <h3 className="text-xl font-medium text-ink mb-3">
                            Built from your wardrobe
                        </h3>
                        <p className="text-base text-ink-tertiary mb-6 max-w-sm">
                            Every suggestion uses clothes you already own. Michela tracks what you haven't worn lately and brings forgotten pieces back into rotation.
                        </p>
                        
                        {/* Wardrobe Grid: Forgotten + Resurfaced */}
                        <div className="flex gap-3 items-end">
                            {/* Forgotten items (faded, grayscale) */}
                            {forgottenItems.map((item, i) => (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.15 + i * 0.08 }}
                                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border border-paper-tertiary"
                                >
                                    <img 
                                        src={item.image} 
                                        alt={item.alt}
                                        className="w-full h-full object-cover grayscale opacity-50"
                                    />
                                </motion.div>
                            ))}
                            
                            {/* Resurfaced item (highlighted) */}
                            <motion.div
                                initial={{ opacity: 0, y: 8, scale: 0.95 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5 }}
                                className="relative"
                            >
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden border-2 border-ink/20 ring-2 ring-ink/5">
                                    <img 
                                        src={resurfacedItem.image} 
                                        alt={resurfacedItem.alt}
                                        className="w-full h-full object-cover sepia-[0.15]"
                                    />
                                </div>
                            </motion.div>
                        </div>
                        
                        {/* Caption */}
                        <motion.p 
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.6 }}
                            className="text-sm text-ink-tertiary mt-4"
                        >
                            Haven't worn in 3 months → back in rotation
                        </motion.p>
                    </motion.div>

                    {/* Proof 3: Style evolves */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        <h3 className="text-xl font-medium text-ink mb-3">
                            Your style, learned over time
                        </h3>
                        <p className="text-base text-ink-tertiary mb-8 max-w-sm">
                            Love an outfit? Skip another? Michela remembers. Over time, suggestions get more "you" — without you having to explain your taste.
                        </p>
                        
                        {/* Single Before/After Slider */}
                        <div className="max-w-sm">
                            {/* Labels */}
                            <div className="flex justify-between mb-3">
                                <span className="text-sm text-ink-tertiary">More relaxed</span>
                                <span className="text-sm text-ink-tertiary">More tailored</span>
                            </div>
                            
                            {/* Track */}
                            <div className="relative h-1.5 bg-paper-tertiary rounded-full">
                                {/* Ghost position (before) */}
                                <motion.div 
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.3 }}
                                    className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-ink-tertiary/30"
                                    style={{ left: '30%' }}
                                />
                                
                                {/* Current position (after) */}
                                <motion.div 
                                    initial={{ left: '30%' }}
                                    whileInView={{ left: '58%' }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.5, duration: 0.9, ease: 'easeOut' }}
                                    className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full bg-ink shadow-sm"
                                />
                            </div>
                            
                            {/* Caption */}
                            <motion.p 
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.8 }}
                                className="text-sm text-ink-tertiary mt-4"
                            >
                                Adjusts as you react
                            </motion.p>
                        </div>
                    </motion.div>

                </div>
            </div>
        </section>
    )
}
